/**
 * @fileOverview This file contains the implementation of the manufacturers datagrid for Inventory module
 * @version 1.0.0
 * @since 1.0.0
 * @author Luis Raul Ariceaga
 */

//#region imports
import { useEffect, useState } from 'react';
import customAxios from '../../utils/customAxios';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import { GlobalVariables } from '../../globalVar';

//MUI imports
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { Tooltip } from '@mui/material';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import { DeleteOutlineOutlined as DeleteIcon } from '@mui/icons-material';

//Translation imports
import { useTranslationHook } from '../../hooks/UseTranslationHook';
import { useTranslation } from 'react-i18next';

//Modals
import ModalAddManufacturers from '../Modal/Create/ModalAddManufacturers';
import ModalUpdateManufacturers from '../Modal/Update/ModalUpdateManufacturers';
import ModalDeleteManufacturers from '../Modal/Delete/ModalDeleteManufacturers';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAdd } from '@fortawesome/free-solid-svg-icons';

import { useTableLocale } from '../../hooks/useTableLocale';
//#endregion

export const ManufacturersTable = () => {
  const [modalAddManufacturerShow, setModalAddManufacturerShow] =
    useState(false);
  const [modalDeleteManufacturerShow, setModalDeleteManufacturerShow] =
    useState(false);
  const [modalUpdateManufacturerShow, setModalUpdateManufacturerShow] =
    useState(false);

  const [manufacturersData, setManufacturersData] = useState([]);

  const [isLoading, setIsLoading] = useState(true);

  const { t } = useTranslation();

  const {
    inventoryManufacturersStr,
    nameString,
    descriptionString,
    statusString,
    createdByString,
    updatedByString,
    createdAtString,
    updatedAtString,
    addString,
    editString,
    deleteString,
    detailString,
  } = useTranslationHook();

  const endpoint = `${GlobalVariables.getURL()}manufacturers`;

  const locale = useTableLocale();

  const getData = async () => {
    setIsLoading(true);

    try {
      const response = await customAxios.get(endpoint, {
        headers: {
          Authorization: 'bearer ' + localStorage.getItem('token'),
        },
      });
      const manufacturersData = response.data.data.map((info) => {
        return {
          ...info,
          is_active: t(`${info.is_active}`),
        };
      });
      setManufacturersData(manufacturersData);
      setIsLoading(false);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {}, [isLoading]);

  //Update
  function manageManufacturerUpdate(manufacturerInfo) {
    console.log('Entro update local s', manufacturerInfo);
    var manufacturerId = manufacturerInfo.id;
    var manufacturerName = manufacturerInfo.name;
    var manufacturerDescription = manufacturerInfo.description;
    localStorage.setItem('manufacturerIdForUpdate', manufacturerId);
    localStorage.setItem('manufacturerNameForUpdate', manufacturerName);
    localStorage.setItem(
      'manufacturerDescriptionForUpdate',
      manufacturerDescription,
    );
  }

  //Delete
  function manageManufacturerDelete(manufacturerInfo) {
    localStorage.setItem('manufacturerIdToDelete', manufacturerInfo.id);
    localStorage.setItem('manufacturerNameToDelete', manufacturerInfo.name);
    localStorage.setItem(
      'manufacturerDescriptionToDelete',
      manufacturerInfo.description,
    );
  }

  // Columns
  const manufacturersColumns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 100,
    },
    {
      field: 'name',
      headerName: nameString,
      width: 200,
    },
    {
      field: 'description',
      headerName: descriptionString,
      width: 300,
    },
    {
      field: 'is_active',
      headerName: statusString,
      width: 150,
      renderCell: (mData) => {
        if (
          mData.row.is_active === 'Activo' ||
          mData.row.is_active === 'Active'
        ) {
          return (
            <div>
              <span className="statusActive">{mData.row.is_active}</span>
            </div>
          );
        } else {
          return (
            <div>
              <span className="statusInactive">{mData.row.is_active}</span>
            </div>
          );
        }
      },
    },
    {
      field: 'created_by',
      headerName: createdByString,
      width: 150,
    },
    {
      field: 'updated_by',
      headerName: updatedByString,
      width: 150,
    },
    {
      field: 'created_at',
      headerName: createdAtString,
      width: 150,
    },
    {
      field: 'updated_at',
      headerName: updatedAtString,
      width: 150,
    },
    // Map through all columns and add default renderCell if not already defined
  ].map((col) =>
    col.renderCell
      ? col
      : {
          ...col,
          renderCell: (params) =>
            ['created_at', 'updated_at'].includes(col.field)
              ? formatDateDDMMYYYY(params.row[col.field])
              : params.row[col.field] || '-',
        },
  );

  //Action column
  const actionColumn = [
    {
      field: 'action',
      headerName: detailString,
      headerClassName: 'equip-datagrid-actions-tour',
      width: 115,
      renderCell: (params) => {
        return (
          <div className="cellAction equip-datagrid-actions-tour">
            <Tooltip title={editString} arrow>
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  marginTop: '5px',
                }}
                onClick={async () => {
                  //Preguntar sobre la respuesta async para ver si los datos pueden no perderse debido a que es asincrono.
                  try {
                    await manageManufacturerUpdate(params.row);
                    // setSelectedManufacturer(params.row);
                    setModalUpdateManufacturerShow(true);
                  } catch (error) {
                    console.log(error);
                  }
                }}
              >
                <ModeEditOutlineOutlinedIcon
                  className="detail-icons"
                  id="update-icon"
                />
              </button>
            </Tooltip>
            <Tooltip title={deleteString} arrow>
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  marginTop: '5px',
                }}
                onClick={() => {
                  setModalDeleteManufacturerShow(true);
                  manageManufacturerDelete(params.row);
                }}
              >
                <DeleteIcon className="detail-icons" id="delete-icon" />
              </button>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="datatable-btns-container">
        <div className="dt-btn-main-container">
          <span className="dabiWebText">{inventoryManufacturersStr} </span>
          <button
            className="add-button create-equip-tour"
            onClick={() => setModalAddManufacturerShow(true)}
          >
            {addString} <FontAwesomeIcon icon={faAdd} />
          </button>
        </div>
      </div>

      {/*Modals*/}
      <ModalAddManufacturers
        show={modalAddManufacturerShow}
        onHide={() => setModalAddManufacturerShow(false)}
        getManufacturers={getData}
      />

      <ModalUpdateManufacturers
        show={modalUpdateManufacturerShow}
        onHide={() => setModalUpdateManufacturerShow(false)}
        getManufacturers={getData}
      />

      <ModalDeleteManufacturers
        show={modalDeleteManufacturerShow}
        onHide={() => setModalDeleteManufacturerShow(false)}
        getManufacturers={getData}
      />

      <DataGridPro
        localeText={locale.components.MuiDataGrid.defaultProps.localeText}
        pagination
        initialState={{
          sorting: {
            sortModel: [{ field: 'id', sort: 'desc' }],
          },
          columns: {
            columnVisibilityModel: {
              id: false,
            },
          },
          pinnedColumns: { right: ['action'] },
        }}
        rows={manufacturersData}
        columns={manufacturersColumns.concat(actionColumn)}
        disableSelectionOnClick
        slots={{ toolbar: GridToolbar }}
      />
    </>
  );
};
